// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Generates a random hexadecimal string of a specified length.
 *
 * @param {number} [length=82] - The length of the hexadecimal string to generate.
 *   Default is `82`
 * @returns {string} A random hexadecimal string of the specified length.
 */

export const generateHexString = (length = 82): string => {
  /**
   * The characters used to construct the hexadecimal string.
   *
   * @type {string}
   */
  const characters = 'abcdef0123456789';

  /**
   * Selects a random character from the given string.
   *
   * @param {string} str - The string to select a character from.
   * @returns {string} A randomly selected character.
   */
  const getRandomChar = (str: string): string =>
    str.charAt(Math.floor(Math.random() * str.length));

  /**
   * Repeats a function call a specified number of times.
   *
   * @param {number} times - How many times to repeat the function call.
   * @param {() => string} fn - The function to call repeatedly.
   * @returns {string[]} An array of results from the repeated function calls.
   */
  const repeat = (times: number, fn: () => string): string[] =>
    Array.from({ length: times }, fn);

  return repeat(length, () => getRandomChar(characters)).join('');
};
